import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { Lock, LockOpen } from 'lucide-react';
import { setActiveBoard } from 'modules/template-editor/store/templates/templates';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarLoader } from 'react-spinners';
import EmptyButton from 'ud-ui/components/empty-button';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import { setBoardLocked } from 'modules/overlay-v2/store/overlay/overlay';
import { selectIsBoardLocked } from 'modules/overlay-v2/store/overlay/selectors';
import Block from '../components/block';
import Text from '../components/text';
import { theme } from 'styles/theme/theme';

export function Dashboard() {
  const { t } = useLingui();
  const dispatch = useDispatch();
  const { boards, activeBoard } = useBoards();
  const isBoardLocked = useSelector(selectIsBoardLocked);

  const baseCallback = useCallback(
    (newState: number | undefined) => {
      if (newState === activeBoard?.id) {
        dispatch(setActiveBoard({ id: undefined }));
      } else {
        dispatch(setActiveBoard({ id: newState }));
      }
    },
    [activeBoard?.id, dispatch],
  );

  const listItems: ItemType[] | undefined = useMemo(() => {
    const listBoards: ItemType[] = boards
      ?.filter((board) => board.type === 'static' || board.type === 'carousel' || board.type === 'tv')
      .map((board) => {
        return { text: board.name, state: board.id };
      });

    listBoards?.push({ text: t`Выкл.`, state: undefined });

    return listBoards;
  }, [boards, t]);

  const items = useMemo(() => {
    return listItems?.map((item) => {
      const onClick = () => {
        baseCallback(item.state);
      };

      return (
        <Item key={item.text} active={activeBoard?.id === item.state} onClick={onClick} className={'flex-center'}>
          <Text color={theme.colors.SurfaceWhite} size={'medium'}>
            {item.text}
          </Text>
        </Item>
      );
    });
  }, [activeBoard?.id, baseCallback, listItems]);

  const toggleLock = useCallback(() => {
    dispatch(setBoardLocked(!isBoardLocked));
  }, [dispatch, isBoardLocked]);

  return (
    <Block className={'flex flex-col'} headerText={t`Табло`}>
      <EmptyButton
        style={{ position: 'absolute', right: 5, top: 5, zIndex: 1 }}
        onClick={toggleLock}
        color={isBoardLocked ? 'white' : 'gray'}
        icon={isBoardLocked ? <Lock size={16} /> : <LockOpen size={16} />}
      />
      <DashboardList className={'flex'}>
        {boards.length === 0 ? (
          <div className={'flex-center w-full'}>
            <BarLoader width={250} />
          </div>
        ) : (
          items
        )}
      </DashboardList>
    </Block>
  );
}

type ItemType = { text: string; state: number | undefined };

const DashboardList = styled.div`
  gap: 8px;
  flex-wrap: wrap;
`;

const Item = styled.div<{ active: boolean }>`
  padding-inline: 14px;
  padding-block: 4px;
  background: ${({ active }) => (active ? theme.colors.AccentNaplesYellow : theme.colors.Gray)};
`;
